import { type FormEvent, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { Mail } from 'lucide-react';
import { Button, Input, cn, Wordmark } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';
import { ApiError } from '@wundo/client/api.ts';
import {
  requestSignInLink,
  signInDestination,
  signInWithCode,
  startGoogleSignIn,
  startMicrosoftSignIn,
  verifySignInToken,
} from '@wundo/client/auth.ts';
import { leaveTo } from '@wundo/client/leaveTo.ts';
import { redirectIfSignedIn } from '@wundo/client/session.ts';
import { GoogleIcon, MicrosoftIcon } from '@/components/ProviderIcons.tsx';
import { SigningIn } from '@/components/SigningIn.tsx';
import { rememberRequestedRedirect } from '@/lib/requestedSignInRedirect.ts';
import { signInReturnTo } from '@/lib/signInReturnTo.ts';

/*
 * The only login screen. The staff console has none of its own — it sends
 * people here with `redirect` set to its own origin, and sign-in returns them
 * there. The session cookie is set on the API origin, which both frontends
 * call, so one sign-in serves both.
 *
 * The value is checked here, because a signed-in visitor is sent straight on
 * to it, and again by the backend before it ends up in a token.
 */
export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: signInReturnTo(search.redirect),
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  /*
   * Someone who already has a session has no use for this screen, so they are
   * sent on to where they were going instead of being asked to sign in again —
   * the same destination sign-in itself would have used.
   */
  beforeLoad: async ({ context, search }) => {
    /*
     * Signing out and an expired session both arrive here without a reload,
     * and the recording is for signed-in use only. The pending segment is
     * dropped so this screen is never uploaded.
     */
    void Sentry.getReplay()?.stop({ flush: false });

    /*
     * A used link is said as such, also to a browser still signed in from it:
     * sent on, a session that may only practise meets a page about Teams.
     */
    if (search.error === 'INVALID_TOKEN') {
      return { signedIn: false };
    }

    return {
      signedIn: await redirectIfSignedIn(
        context.queryClient,
        signInDestination(search.redirect),
      ),
    };
  },
});

/* Reading the search is the route's job, so the screen itself stays renderable. */
function LoginRoute() {
  const { redirect, error } = Route.useSearch();
  const { signedIn } = Route.useRouteContext();

  /* The browser is on its way out; the form would be a flash of the wrong screen. */
  if (signedIn) {
    return <SigningIn />;
  }

  return <LoginPage redirect={redirect} error={error} />;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function BrandPanel() {
  return (
    <div className='relative flex flex-col overflow-hidden bg-navy px-14 py-12 text-white max-agenda:hidden'>
      {/* Glow tuned to the design's 55% setting; see .login-brand-glow in app.css. */}
      <div className='login-brand-glow pointer-events-none absolute inset-0' />
      <div className='relative z-10 m-auto flex flex-col gap-6.5'>
        <img src='/logo-green.png' alt='Wundo' className='block h-auto w-40' />
        <span className='h-0.5 w-22 rounded-full bg-green' />
      </div>
    </div>
  );
}

/*
 * Two requests behind one button: the code names the token the link carries,
 * and the token still has to go through the door. So this form meets the
 * door's refusals as well as its own — a dead or foreign token is not a
 * mistyped code, and saying "that code is wrong" would send someone hunting
 * for a typo that is not there.
 */
function typedCodeErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return m.login_code_invalid();
  }
  if (caught.code === 'INVALID_TOKEN' || caught.code === 'LINK_OTHER_DEVICE') {
    return refusedSignInMessage(caught.code);
  }
  if (caught.type === 'rate_limited') {
    return m.login_error_rate_limited();
  }
  return m.login_code_invalid();
}

/*
 * The mail's link signs its reader in with one press, so this screen mostly waits.
 * The code form is for the one case the link cannot serve: the mail is open
 * on a different device than the one sitting at this screen.
 */
function SentConfirmation({ email, onReset }: { email: string; onReset: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!code.trim()) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      const token = await signInWithCode(email, code);
      /*
       * The same door the link's button posts to, and the same token: where it
       * lands was settled when the mail was sent. The browser is on its way
       * out.
       */
      const { destination } = await verifySignInToken(token);
      leaveTo(destination);
    } catch (caught) {
      setError(typedCodeErrorMessage(caught));
      setLoading(false);
    }
  }

  return (
    <div className='flex animate-pop flex-col items-center gap-4.5 text-center'>
      <div className='flex size-16.5 items-center justify-center rounded-xl bg-type-func-tint text-type-func'>
        <Mail className='size-7.5' aria-hidden='true' />
      </div>
      <h1 className='text-xl font-semibold tracking-tight'>{m.login_sent_title()}</h1>
      <p className='max-w-[32ch] text-md text-ink-muted'>
        {m.login_sent_body({ email })}
      </p>
      <form className='flex w-full flex-col gap-2.75' onSubmit={handleSubmit} noValidate>
        <div>
          <label
            className='mb-1.75 block text-sm font-semibold text-ink-secondary'
            htmlFor='code'
          >
            {m.login_code_label()}
          </label>
          <Input
            id='code'
            type='text'
            fieldSize='lg'
            autoComplete='one-time-code'
            autoCapitalize='characters'
            spellCheck={false}
            maxLength={6}
            value={code}
            invalid={Boolean(error)}
            onChange={(event) => {
              setCode(event.target.value);
              if (error) {
                setError('');
              }
            }}
          />
        </div>
        {error && <span className='text-sm font-medium text-danger'>{error}</span>}
        <Button
          type='submit'
          variant='accent'
          shape='block'
          size='lg'
          fullWidth
          loading={loading}
        >
          {m.login_code_submit()}
        </Button>
      </form>
      <Button type='button' variant='ghost' size='sm' onClick={onReset}>
        {m.login_sent_other_email()}
      </Button>
    </div>
  );
}

/*
 * Both refusals name what the person can do about it, rather than showing a
 * generic failure they cannot act on.
 *
 * SSO_REQUIRED: their organisation has turned sign-in links off, so the backend
 * will not email one. Which providers it leaves them is a separate pair of
 * switches, so the message names none of them and points at the buttons, which
 * are on the same screen.
 *
 * UNKNOWN_ACCOUNT: no account for that address. Everyone is provisioned up
 * front, so this is a typo or a personal mailbox; saying so beats leaving them
 * on "check your inbox" waiting for mail that was never sent.
 */
function signInErrorMessage(caught: unknown): string {
  if (!(caught instanceof ApiError)) {
    return m.login_error_generic();
  }
  if (caught.code === 'SSO_REQUIRED') {
    return m.login_sso_required();
  }
  if (caught.code === 'UNKNOWN_ACCOUNT') {
    return m.login_unknown_account();
  }
  if (caught.type === 'rate_limited') {
    return m.login_error_rate_limited();
  }
  return m.login_error_generic();
}

/*
 * A sign-in that failed somewhere else — at a provider, or on an emailed link —
 * sends the person back here with the reason in the URL. Only the reasons
 * someone can act on get their own line; the rest read as a generic failure,
 * which is all we could honestly tell them.
 *
 * The two that actually happen:
 *
 * `signup_disabled` — the provider authenticated them fine, but nobody has
 * provisioned that address here. Saying "something went wrong" sends people off
 * to debug a Google account that is working perfectly.
 *
 * `INVALID_TOKEN` — the link expired or was already used. Without saying so,
 * people click the same dead link out of the same email again.
 *
 * `account_not_linked` — under this configuration it has exactly one cause: the
 * address is one Wundo never vouched for, which is true only of addresses typed
 * into a chat. One sign-in by link settles it permanently, so the message says
 * that rather than describing the state the person is stuck in.
 */
function refusedSignInMessage(code: string | undefined): string {
  if (!code) {
    return '';
  }
  if (code === 'INVALID_TOKEN') {
    return m.login_link_expired();
  }
  /*
   * The link was opened in a browser that did not request it — usually the
   * mail was read on another device. The link still works where it was asked
   * for; here, the code from the same mail is the way in.
   */
  if (code === 'LINK_OTHER_DEVICE') {
    return m.login_link_other_device();
  }
  /* The same situation the magic link reports, reached by a different door. */
  if (code === 'signup_disabled' || code === 'new_user_signup_disabled') {
    return m.login_unknown_account();
  }
  if (code === 'MICROSOFT_DOMAIN_UNVERIFIED') {
    return m.login_microsoft_unverified();
  }
  if (code === 'account_not_linked') {
    return m.login_provider_not_linked();
  }
  if (code === 'access_denied') {
    return m.login_access_denied();
  }
  return m.login_error_generic();
}

export function LoginPage({
  redirect,
  error: errorCode,
}: {
  redirect?: string;
  error?: string;
}) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const refusal = refusedSignInMessage(errorCode);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email)) {
      setError(m.login_email_invalid());
      return;
    }

    setError('');
    setLoading(true);
    try {
      await requestSignInLink(email, redirect);
      /* Reached only once the backend accepted the address, so a link is on its way. */
      rememberRequestedRedirect(redirect);
      setSent(true);
    } catch (caught) {
      setError(signInErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  /* On success the browser navigates away, so loading is only cleared on failure. */
  async function handleSocial(start: () => Promise<string>) {
    setError('');
    setLoading(true);
    try {
      window.location.href = await start();
    } catch {
      setError(m.login_error_generic());
      setLoading(false);
    }
  }

  return (
    <div className='grid h-dvh grid-cols-2 overflow-hidden max-agenda:grid-cols-1'>
      <BrandPanel />
      <div className='flex items-center justify-center overflow-y-auto px-7 py-10'>
        <div className='flex w-full max-w-98 flex-col'>
          <div className='mb-7.5 hidden justify-center max-agenda:flex'>
            <Wordmark registered className='h-8.5' />
          </div>

          {sent ? (
            <SentConfirmation email={email} onReset={() => setSent(false)} />
          ) : (
            <>
              <div>
                <h1 className='mb-1.5 text-xl font-semibold tracking-tight'>
                  {m.login_title()}
                </h1>
                <p className='text-md text-ink-muted'>{m.login_subtitle()}</p>
              </div>

              {/* Its own slot: this failure happened elsewhere, unlike the form errors below. */}
              {refusal && (
                <p
                  role='alert'
                  className='mt-6.5 rounded-lg border border-danger-line bg-danger-fill px-4 py-3 text-sm font-medium text-danger'
                >
                  {refusal}
                </p>
              )}

              <div className={cn('flex flex-col gap-2.5', refusal ? 'mt-4' : 'mt-6.5')}>
                <Button
                  type='button'
                  variant='quiet'
                  shape='block'
                  size='lg'
                  fullWidth
                  onClick={() => handleSocial(() => startGoogleSignIn(redirect))}
                  disabled={loading}
                >
                  <GoogleIcon />
                  {m.login_google()}
                </Button>
                <Button
                  type='button'
                  variant='quiet'
                  shape='block'
                  size='lg'
                  fullWidth
                  onClick={() => handleSocial(() => startMicrosoftSignIn(redirect))}
                  disabled={loading}
                >
                  <MicrosoftIcon />
                  {m.login_microsoft()}
                </Button>
              </div>

              <div className='my-5.5 flex items-center gap-3.5 text-xs font-semibold tracking-wide text-ink-muted uppercase'>
                <span className='h-px flex-1 bg-line' />
                {m.login_divider()}
                <span className='h-px flex-1 bg-line' />
              </div>

              <form className='flex flex-col gap-2.75' onSubmit={handleSubmit} noValidate>
                <div>
                  <label
                    className='mb-1.75 block text-sm font-semibold text-ink-secondary'
                    htmlFor='email'
                  >
                    {m.login_email_label()}
                  </label>
                  <Input
                    id='email'
                    type='email'
                    fieldSize='lg'
                    placeholder={m.login_email_placeholder()}
                    value={email}
                    invalid={Boolean(error)}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      if (error) {
                        setError('');
                      }
                    }}
                  />
                </div>
                {error && (
                  <span className='text-sm font-medium text-danger'>{error}</span>
                )}
                <Button
                  type='submit'
                  variant='accent'
                  shape='block'
                  size='lg'
                  fullWidth
                  loading={loading}
                >
                  {m.login_submit()}
                </Button>
              </form>

              <div className='mt-7.5 flex justify-center gap-4.5 text-xs text-ink-muted'>
                <a
                  className='hover:text-ink-muted'
                  href='https://wundo.ai/privacy-policy'
                >
                  {m.login_privacy()}
                </a>
                <a className='hover:text-ink-muted' href='https://wundo.ai/trust-center/'>
                  {m.login_trust_center()}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
